// src/oura/merchant/drawers/PagesDrawer.tsx
//
// "Your pages": what is live, what comes next, and how many of the 50
// questions the site answers. Opened from the Pages published tile on Work.
//
// The page titles are optional and absent on live today (BE handoff
// "Published and upcoming page list"); the server sends counts only. Without
// the list the drawer states the counts and names no page.
//
// "Your site answers N of 50" is the Coverage Set (`stats.coverage`), moved
// here from Progress because it is the number our pages move. It is how many
// questions the site answers, never how often AI names the business (that is
// the AI check). Visibility's "Your site doesn't answer" reads a different
// measure, taken at every scan (`answerCoverage`), and the two can differ. No change is shown against the previous reading: nothing on the
// wire says both were judged on the same questions, and a change across two
// question sets is not a change (rule 2).

import { CheckIcon } from "lucide-react";
import type { ClientPageList, ClientStats } from "@service/aeo/clientReview";
import { Dot, Drawer, DrawerBlock, DrawerHead, ListRow, OfValue, TYPE, Tag } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";

export function PagesDrawer({
    open,
    onClose,
    published,
    units,
    pages,
    coverage,
    coverageAbsent,
}: {
    open: boolean;
    onClose: () => void;
    published: number;
    /** Pages sold for the term. 0 until the build is agreed. */
    units: number;
    pages: ClientPageList | undefined;
    coverage: ClientStats["coverage"];
    coverageAbsent: ClientStats["coverageAbsent"];
}) {
    const upcoming = pages?.upcoming ?? [];
    // One go-live date for all of them heads the list; several go on each row.
    const sameDue = upcoming.every((p) => p.dueAt === upcoming[0]?.dueAt);
    const sharedDay = sameDue ? dayDate(upcoming[0]?.dueAt) : null;
    const checked = dayDate(coverage?.measuredAt);
    const later = units - published - upcoming.length;
    return (
        <Drawer open={open} onClose={onClose}>
            <DrawerHead
                title="Your pages"
                status={{ label: units > 0 ? `${published} of ${units} published this term` : "Set when your build is agreed" }}
            />
            {pages ? (
                <>
                    {pages.published.length > 0 && (
                        <DrawerBlock label="Live">
                            {pages.published.map((p, i) => (
                                <ListRow
                                    key={`${p.title}-${i}`}
                                    icon={<CheckIcon aria-hidden size={16} strokeWidth={2.5} className="text-emerald-500" />}
                                    title={p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline">{p.title}</a> : p.title}
                                    tags={p.kind ? <Tag tone={p.kind === "new" ? "teal" : "gray"}>{p.kind === "new" ? "New" : "Improved"}</Tag> : undefined}
                                    meta={dayDate(p.publishedAt)}
                                />
                            ))}
                        </DrawerBlock>
                    )}
                    {upcoming.length > 0 && (
                        <DrawerBlock label={sharedDay ? `Next, live ${sharedDay}` : "Next"}>
                            {upcoming.map((p, i) => (
                                <ListRow
                                    key={`${p.title}-${i}`}
                                    icon={<Dot tone="blue" />}
                                    title={p.title}
                                    meta={sameDue ? undefined : dayDate(p.dueAt) ?? "Date to be set"}
                                />
                            ))}
                            {later > 0 && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{later} more later this term.</p>}
                        </DrawerBlock>
                    )}
                    {upcoming.length === 0 && later > 0 && <DrawerBlock>{later} more to come this term.</DrawerBlock>}
                </>
            ) : (
                units > 0 && (
                    <DrawerBlock>
                        {published} of {units} pages are live.
                        {units > published && ` ${units - published} more to come this term.`}
                    </DrawerBlock>
                )
            )}
            <DrawerBlock label="Your site answers">
                {coverage ? (
                    <>
                        <p className={cn(TYPE.tt, "m-0 tabular-nums")}>
                            <OfValue n={coverage.answered} of={coverage.total} unit="questions" />
                        </p>
                        <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>
                            Of the questions people ask Google and AI about a place like yours, how many a page on your site answers. Judged once a month.
                            {checked && ` Checked ${checked}.`}
                        </p>
                    </>
                ) : (
                    <p className="m-0 text-gray-600">
                        {/* The server's reason (ourteam-backend#5076, staging f73b9933): no
                            scan yet means wait for the first; a scan that produced only
                            the lexical score means the next one fills it. */}
                        {coverageAbsent === "no_assessment"
                            ? "Not measured yet. We measure it from your first scan."
                            : coverageAbsent === "not_judged"
                                ? "Not measured yet. Counted on your next scan."
                                : "Not measured yet."}
                    </p>
                )}
            </DrawerBlock>
        </Drawer>
    );
}
